import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import { CodeBlock } from "@/components/storyboard/code/CodeBlock";
import CodeQuizEditor from "@/components/storyboard/code/CodeQuizEditor";
import { CodeQuizWorkspace } from "@/components/storyboard/code/CodeQuizWorkspace";
import { CodeQuizActions } from "@/components/storyboard/quiz/CodeQuizActions";
import { codeQuizReview } from "@/components/storyboard/quiz/CodeQuizVerdict";
import { QuizChallengeHeading } from "@/components/storyboard/quiz/QuizChallengeHeading";
import {
  deriveDefaultLanguage,
  stripCodeFence,
} from "@/components/storyboard/code/codeUtils";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { ValidationSubmission } from "@/lib/validationSubmission";
import { useQuizCodeState } from "@/hooks/useQuizCodeState";
import { cn } from "@/lib/utils";
import type { QuizCodeAssertionEntryData } from "@/types/storyboard";
import {
  collectsWithoutVerdict,
  type HiddenVerdictProps,
} from "@/lib/quizCollect";

export interface QuizCodeAssertionProps extends HiddenVerdictProps {
  entries: QuizCodeAssertionEntryData[];
  /** Submits the code for server-side (LLM) validation; also persists it. */
  onValidate: (code: string, entryIndex: number) => Promise<ValidationResult>;
  /** Saved submissions per entry index, used to re-seed the editor on reopen. */
  initialSubmissions?: Record<number, ValidationSubmission>;
  /** Called after a submission is persisted, so saved state can be refreshed. */
  onSubmitted?: () => void;
}

const QuizCodeAssertion = ({
  entries,
  onValidate,
  initialSubmissions,
  onSubmitted,
  revealFeedback,
  advanceLabel,
  onAdvance,
}: QuizCodeAssertionProps) => {
  // Collapsing the goal hands the whole left column to the assertions, which is
  // what learners want once they have read the task.
  const [isGoalOpen, setIsGoalOpen] = useState(true);

  const defaultLanguage = deriveDefaultLanguage(
    entries.flatMap((e) => [e.codeStub, e.testCode, e.solution])
  );

  const quiz = useQuizCodeState({
    entries,
    initialSubmissions,
    onValidate,
    onSubmitted,
    revealFeedback,
    onAdvance,
    getDefaultInput: (entry) => stripCodeFence(entry.codeStub),
  });
  const {
    currentEntry,
    currentInput: currentCode,
    currentResult,
    isSettled,
    isValidating,
    handleInputChange: handleCodeChange,
  } = quiz;

  if (!entries || entries.length === 0) return null;
  if (!currentEntry) return null;

  const meta = (
    <>
      <QuizChallengeHeading challenge={currentEntry.challenge} />
      {currentEntry.implementationGoal && (
        <div>
          <button
            type="button"
            onClick={() => setIsGoalOpen((open) => !open)}
            aria-expanded={isGoalOpen}
            className="flex w-full cursor-pointer items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 transition-colors hover:text-gray-700"
          >
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 transition-transform",
                !isGoalOpen && "-rotate-90"
              )}
              aria-hidden="true"
            />
            Ziel
          </button>
          {isGoalOpen && (
            <div className="mt-2 text-sm text-gray-700 prose max-w-full">
              <CourseMarkdown>{currentEntry.implementationGoal}</CourseMarkdown>
            </div>
          )}
        </div>
      )}
    </>
  );

  const reference = (
    <div className="overflow-hidden rounded-xl border border-secondary/40 bg-secondary/10">
      <div className="border-b border-secondary/40 px-4 py-2">
        <span className="text-xs font-semibold text-secondary uppercase tracking-wide">
          Diese Bedingungen müssen erfüllt sein
        </span>
      </div>
      <div className="p-3">
        <CodeBlock
          code={currentEntry.testCode}
          defaultLanguage={defaultLanguage}
        />
      </div>
    </div>
  );

  const editor = (
    <CodeQuizEditor
      label="Dein Code"
      value={currentCode}
      onChange={handleCodeChange}
      language={defaultLanguage}
      readOnly={isValidating || isSettled}
    />
  );

  const actions = (
    <CodeQuizActions
      quiz={quiz}
      submitLabel={
        collectsWithoutVerdict(revealFeedback)
          ? (advanceLabel ?? "Nächste Frage")
          : "Code prüfen"
      }
      canSubmit={currentCode.trim().length > 0}
    />
  );

  return (
    <CodeQuizWorkspace
      meta={meta}
      reference={reference}
      feedback={codeQuizReview(
        currentResult,
        "Nutze das Feedback und überarbeite deinen Code – er bleibt erhalten."
      )}
      editor={editor}
      actions={actions}
    />
  );
};

export default QuizCodeAssertion;
